import { useRouter } from "next/router";
import { useContext } from "react";
import { CookieContext } from "../../context/cookieContext";
import { V3HasAccessToBillingPage } from "../../pagelayouts/settings/billing/payment.utils";
import { theme } from "../../theme/theme";
import { APPLICATION_ROUTES } from "../../shared-logic/routes";
import { getAuthenticatedUserClient } from "../../utils/getAuthenticatedUser";
import {
    V3IconMyAccount,
    V3IconSources,
    V3IconCard,
    V3IconBell,
    V3IconOut,
    V3IconGift,
    V3IconSettings,
    V3IconPortfolio,
    V3IconCertificates,
    V3IconPlugin,
    V3IconSingleLink
} from "../Icons";
import { V3Body } from "../Typography";
import { SettingsLinkPanel, V3LinksPanel } from "./V3LinksPanel";
import { LinksPanelLinkWrapper } from "./V3LinksPanel.styles";
import { useSignOut } from "../../hooks/useSignOut";
import { AccessLevel, UserAccountType } from "@authory-packages/core";
import { canUseProFeature } from "../../utils/canUseProFeature";

export const V3SettingsLinksPanel = () => {
    const router = useRouter();
    const signOut = useSignOut();
    const token = useContext(CookieContext);
    const {
        authenticatedUser: { data }
    } = getAuthenticatedUserClient(token, router, true);
    const showBillingPage = V3HasAccessToBillingPage(data);
    const isBusinessParent = data?.type === UserAccountType.BusinessParent;
    const isPro = data && canUseProFeature(data.planSettings);
    const isStandard = data && data.planSettings.accessLevel === AccessLevel.Standard;
    const canUseCustomDomain = isPro || isStandard;

    const links: SettingsLinkPanel[] = [
        {
            icon: <V3IconMyAccount />,
            label: "Account",
            href: APPLICATION_ROUTES.SETTINGS_ACCOUNT
        }
    ];

    if (!isBusinessParent) {
        links.push({
            icon: <V3IconSources />,
            label: "Sources",
            href: APPLICATION_ROUTES.SETTINGS_SOURCES
        });

        links.push({
            icon: <V3IconSingleLink width="18" height="18" stroke={"#ABB0BA"} />,
            label: "Smart Share Links",
            href: APPLICATION_ROUTES.ANALYTICS_SMART_SHARE
        });
    }

    if (showBillingPage) {
        links.push({
            icon: <V3IconCard />,
            label: "Billing",
            href: APPLICATION_ROUTES.SETTINGS_BILLING
        });
    }

    if (!isBusinessParent) {
        links.push({
            icon: <V3IconCertificates stroke="#ABB0BA" />,
            label: "Certificates",
            href: APPLICATION_ROUTES.SETTINGS_CERTIFICATES
        });
    }

    if (!isBusinessParent) {
        links.push({
            icon: <V3IconGift stroke="#ABB0BA" />,
            label: "Refer a Friend",
            href: APPLICATION_ROUTES.SETTINGS_REFERRAL
        });
        links.push({
            icon: <V3IconPlugin size={16} strokeWidth="2.3" />,
            label: "Browser Extension",
            href: APPLICATION_ROUTES.SETTINGS_PLUGIN
        });
    }

    links.push({
        icon: <V3IconBell />,
        label: "Notifications",
        href: APPLICATION_ROUTES.SETTINGS_NOTIFICATIONS
    });

    if (!isBusinessParent) {
        links.push({
            icon: <V3IconSettings />,
            label: "Integrations",
            href: APPLICATION_ROUTES.SETTINGS_WEBHOOKS,
            inactive: !isPro,
            proTooltip: !isPro,
            proTooltipContent: <>Zapier integration and API access are part of the Pro plan.</>
        });

        links.push({
            icon: <V3IconPortfolio width={16} height={16} />,
            label: "Custom domain",
            href: APPLICATION_ROUTES.SETTINGS_CUSTOM_DOMAINS,
            inactive: !canUseCustomDomain,
            proTooltip: !canUseCustomDomain,
            proTooltipContent: <>Custom domain support is part of the Pro and Standard plans.</>
        });
    }

    return (
        <>
            <V3LinksPanel links={links}>
                <LinksPanelLinkWrapper $baseColor={theme.v3.colors.red}>
                    <div>
                        <V3IconOut />
                    </div>
                    <div>
                        <V3Body onClick={signOut}>
                            <strong>Sign out</strong>
                        </V3Body>
                    </div>
                </LinksPanelLinkWrapper>
            </V3LinksPanel>
        </>
    );
};
